import { compareGauges, computeRainTotal, DPR_ARCHIVE_START_MS, estimateRainRun, fetchRainSeries, MAX_WINDOW_MS } from "./rainClient.js";
import { createGaugeLayer, gaugeBias } from "./gaugeLayer.js";
import { loadGaugeTotals } from "./gauges.js";
import { cumAt } from "./pointSeries.js";
import { fetchPfds, formatPfdsLine } from "./pfds.js";
import { createRainLayer, formatRain, formatRainRange } from "./rainLayer.js";
import { cumulativeBand, intervalAt, rangeMayBeNarrow } from "./uncertainty.js";

function zoneOffsetMs(utcMs, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(new Date(utcMs));
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  return asUtc - Math.floor(utcMs / 1000) * 1000;
}

// "2024-05-06T21:30" in the station's zone -> UTC ms.
export function zonedInputToUtcMs(value, timeZone) {
  const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return NaN;
  const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  if (!timeZone) return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime();
  let utc = wall - zoneOffsetMs(wall, timeZone);
  utc = wall - zoneOffsetMs(utc, timeZone);
  return utc;
}

export function utcMsToZonedInput(utcMs, timeZone) {
  const local = timeZone ? utcMs + zoneOffsetMs(utcMs, timeZone) : utcMs - new Date(utcMs).getTimezoneOffset() * 60000;
  return new Date(local).toISOString().slice(0, 16);
}

function formatWindow(startMs, endMs, timeZone) {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone, month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short",
  });
  return `${fmt.format(new Date(startMs))} – ${fmt.format(new Date(endMs))}`;
}

function formatClock(ms, timeZone) {
  return new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" }).format(new Date(ms));
}

function formatLatLon({ lat, lng }) {
  return `${Math.abs(lat).toFixed(3)}°${lat >= 0 ? "N" : "S"} ${Math.abs(lng).toFixed(3)}°${lng >= 0 ? "E" : "W"}`;
}

// Wires the rain-total modal, map layer, summary card and point chart.
// getContext() must return { station, timeMs } for what is on screen;
// onChange() fires when the shareable state (window, pinned point) changes.
export function initRainPanel({ L, map, getContext, onStart, onEnter, onExit, onChange }) {
  const $ = (id) => document.getElementById(id);
  const dom = {
    openBtn: $("rainTotalBtn"),
    modal: $("rainModal"),
    backdrop: $("rainBackdrop"),
    close: $("rainClose"),
    station: $("rainStation"),
    start: $("rainStart"),
    end: $("rainEnd"),
    estimate: $("rainEstimate"),
    error: $("rainError"),
    progress: $("rainProgress"),
    run: $("rainRun"),
    cancel: $("rainCancel"),
    summary: $("rainSummary"),
    summaryText: $("rainSummaryText"),
    summaryClose: $("rainSummaryClose"),
    gauges: $("rainGauges"),
    units: $("rainUnits"),
    chart: $("rainChart"),
    chartTitle: $("rainChartTitle"),
    chartBody: $("rainChartBody"),
    chartClose: $("rainChartClose"),
    pfds: $("rainChartPfds"),
  };
  if (!dom.openBtn || !dom.modal) return null;

  const layer = createRainLayer(L, map, { onPick: (latlng) => pinPoint(latlng) });
  let controller = null;
  let estimateToken = 0;
  let units = "in";
  let chart = null;
  let pinned = null; // { latlng, marker, series, pfds }
  let seriesToken = 0;
  let pfdsToken = 0;
  let pfdsController = null;
  const gaugeLayer = createGaugeLayer(L, map, { onPick: (latlng) => pinPoint(latlng) });
  let gaugeController = null;
  let gaugeStatus = { state: "idle" };

  const ctx = () => getContext() || {};
  const tz = () => ctx().station?.timezone;

  function readWindow() {
    return { startMs: zonedInputToUtcMs(dom.start.value, tz()), endMs: zonedInputToUtcMs(dom.end.value, tz()) };
  }

  function validate({ startMs, endMs }) {
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return "Choose a start and end time.";
    if (endMs <= startMs) return "End time must be after start time.";
    if (endMs - startMs > MAX_WINDOW_MS) return "Choose a window of 72 hours or less.";
    if (endMs < DPR_ARCHIVE_START_MS) return "Dual-pol rain rates are archived from April 2020 onward.";
    return "";
  }

  async function refreshEstimate() {
    const token = ++estimateToken;
    const win = readWindow();
    const problem = validate(win);
    dom.error.textContent = problem;
    dom.run.disabled = Boolean(problem);
    dom.estimate.textContent = "";
    if (problem) return;
    dom.estimate.textContent = "Checking available scans…";
    try {
      const est = await estimateRainRun({ station: ctx().station, ...win });
      if (token !== estimateToken) return;
      dom.estimate.textContent = `${est.frames} scans · ${(est.bytes / 1e6).toFixed(1)} MB`;
    } catch {
      if (token === estimateToken) dom.estimate.textContent = "";
    }
  }

  function open() {
    const { station, timeMs } = ctx();
    if (!station) return;
    const end = Number.isFinite(timeMs) ? timeMs : Date.now();
    dom.station.textContent = station.label || station.id;
    dom.end.value = utcMsToZonedInput(end, station.timezone);
    dom.start.value = utcMsToZonedInput(end - 6 * 3600 * 1000, station.timezone);
    dom.progress.hidden = true;
    dom.cancel.hidden = true;
    dom.modal.hidden = false;
    refreshEstimate();
  }

  function closeModal() {
    controller?.abort();
    dom.modal.hidden = true;
  }

  function renderChart() {
    const result = layer.result;
    if (!chart || !pinned?.series || !result) return;
    const { series, latlng } = pinned;
    const iv = Number.isFinite(series.total) ? intervalAt(result, latlng.lat, latlng.lng) : null;
    const range = iv
      ? ` · gauge likely ${formatRainRange(iv.q05, iv.q95, units)}${rangeMayBeNarrow(result) ? " (may be too narrow: radar gap)" : ""}`
      : "";
    dom.chartTitle.innerHTML = Number.isFinite(series.total)
      ? `<strong>${formatRain(series.total, units)}</strong>${range} · ${formatLatLon(latlng)}`
      : `Outside radar coverage · ${formatLatLon(latlng)}`;
    const g = gaugeLayer.nearest(latlng.lat, latlng.lng);
    let gauge = null;
    if (g && Number.isFinite(series.total)) {
      // Start the gauge where the radar total stood at the gauge's first ob,
      // so both lines are comparable over the gauge's own window.
      const t = [g.w0];
      const v = [cumAt(series.cum, g.w0)];
      for (const h of g.hourly) {
        t.push(h.w1);
        v.push(v[v.length - 1] + h.mm);
      }
      gauge = { t, v };
      const radarSame = cumAt(series.cum, g.w1) - cumAt(series.cum, g.w0);
      dom.chartTitle.innerHTML += `<div class="rain-chart-gauge"><span class="rain-gauge-key"></span>Gauge ${g.id} (${g.km.toFixed(1)} km): ${formatRain(g.mm, units)} vs radar ${formatRain(radarSame, units)} over ${formatClock(g.w0, tz())} – ${formatClock(g.w1, tz())}</div>`;
    }
    const band = Number.isFinite(series.total) ? cumulativeBand(result, latlng.lat, latlng.lng, series.cum) : null;
    chart.render(series, { startMs: result.startMs, endMs: result.endMs, timeZone: tz(), units, gauge, band });
    showPfds();
  }

  function clearPfds() {
    pfdsToken++;
    pfdsController?.abort();
    pfdsController = null;
    if (dom.pfds) dom.pfds.textContent = "";
  }

  function showPfds() {
    if (!dom.pfds || !pinned?.pfds) return;
    const table = pinned.pfds;
    if (!table.ok) {
      dom.pfds.textContent = formatPfdsLine(table);
      return;
    }
    const result = layer.result;
    const series = pinned.series;
    if (!result || !Number.isFinite(series?.total)) {
      if (series && !Number.isFinite(series.total)) dom.pfds.textContent = "";
      return;
    }
    dom.pfds.textContent = formatPfdsLine(table, {
      totalMm: series.total,
      durationMs: result.endMs - result.startMs,
      units,
    });
  }

  function loadPfds(latlng) {
    pfdsController?.abort();
    const controller = new AbortController();
    pfdsController = controller;
    const token = ++pfdsToken;
    if (dom.pfds) dom.pfds.textContent = "Checking NOAA Atlas 14…";
    fetchPfds(latlng.lat, latlng.lng, { signal: controller.signal }).then((table) => {
      if (token !== pfdsToken || !pinned) return;
      pinned.pfds = table;
      showPfds();
    }).catch((err) => {
      if (err?.name === "AbortError" || token !== pfdsToken) return;
      if (dom.pfds) dom.pfds.textContent = "Frequency data unavailable";
    });
  }

  async function loadGauges(result) {
    gaugeController?.abort();
    gaugeLayer.clear();
    if (result.endMs - result.startMs < 60 * 60 * 1000) {
      gaugeStatus = { state: "short" };
      return;
    }
    const controller = new AbortController();
    gaugeController = controller;
    gaugeStatus = { state: "loading" };
    renderSummary(result);
    try {
      const rows = await loadGaugeTotals({ station: ctx().station, startMs: result.startMs, endMs: result.endMs, signal: controller.signal });
      const ok = rows.filter((r) => r.status === "ok");
      const compared = new Map((ok.length ? await compareGauges(ok) : []).map((c) => [c.id, c]));
      if (controller.signal.aborted || layer.result !== result) return;
      const merged = rows.map((r) => ({ ...r, ...compared.get(r.id) }));
      gaugeLayer.show(merged, { timeZone: tz() });
      gaugeStatus = { state: "done", total: ok.length, ...gaugeBias(merged) };
      renderSummary(result);
      renderChart();
    } catch (err) {
      if (err?.name === "AbortError") return;
      gaugeStatus = { state: "error" };
      renderSummary(result);
    }
  }

  function gaugeSummaryHtml() {
    if (gaugeStatus.state === "loading") return "<div>Loading nearby gauges…</div>";
    if (gaugeStatus.state === "error") return "<div>Gauge data unavailable</div>";
    if (gaugeStatus.state !== "done") return "";
    if (!gaugeStatus.total) return "<div>No usable gauges in range</div>";
    const bias = gaugeStatus.n
      ? ` · radar/gauge ${gaugeStatus.ratio.toFixed(2)}× at ${gaugeStatus.n} wet`
      : " · all dry";
    return `<div>${gaugeStatus.total} gauges${bias}</div>`;
  }

  async function pinPoint(latlng) {
    if (!layer.result) return;
    unpin(false);
    const token = ++seriesToken;
    pinned = { latlng, marker: L.circleMarker(latlng, { radius: 6, color: "#ffffff", weight: 2, fillColor: "#5aa9ff", fillOpacity: 1 }).addTo(map) };
    dom.chart.hidden = false;
    dom.chart.parentElement?.classList.add("rain-chart-open");
    dom.chartTitle.textContent = `Loading · ${formatLatLon(latlng)}`;
    loadPfds(latlng);
    onChange?.();
    try {
      const [{ createRainChart }, series] = await Promise.all([
        import("./rainChart.js"),
        fetchRainSeries({ lat: latlng.lat, lon: latlng.lng }),
      ]);
      if (token !== seriesToken || !pinned) return;
      if (!chart) chart = createRainChart(dom.chartBody);
      pinned.series = series;
      renderChart();
    } catch (err) {
      if (token === seriesToken) dom.chartTitle.textContent = err?.message || String(err);
    }
  }

  function unpin(notify = true) {
    seriesToken++;
    clearPfds();
    if (pinned?.marker) map.removeLayer(pinned.marker);
    pinned = null;
    chart?.destroy();
    chart = null;
    dom.chart.hidden = true;
    dom.chart.parentElement?.classList.remove("rain-chart-open");
    if (notify) onChange?.();
  }

  function exitRainMode() {
    unpin(false);
    gaugeController?.abort();
    gaugeLayer.clear();
    gaugeStatus = { state: "idle" };
    layer.clear();
    dom.summary.hidden = true;
    onExit?.();
    onChange?.();
  }

  function renderSummary(result) {
    const gapMin = result.gaps.reduce((n, g) => n + (g.endMs - g.startMs), 0) / 60000;
    let max = 0;
    for (const v of result.total) if (v > max) max = v;
    dom.summaryText.innerHTML = `
      <div class="rain-summary-title">Rain total · ${ctx().station?.id || ""}</div>
      <div>${formatWindow(result.startMs, result.endMs, tz())}</div>
      <div>Max ${formatRain(max, units)} · ${result.frameTimes.length} scans (${result.source}${result.method === "advection" ? ", motion-corrected" : ""})</div>
      ${gaugeSummaryHtml()}
      <div class="rain-summary-hint">Hover for totals and likely gauge ranges · click or tap for a chart</div>
      ${gapMin >= 1 ? `<div class="rain-summary-warn">${Math.round(gapMin)} min without radar data · gauge ranges may be too narrow</div>` : ""}`;
    dom.summary.hidden = false;
  }

  async function run(preset = null) {
    const win = preset || readWindow();
    const problem = validate(win);
    if (problem) {
      dom.error.textContent = problem;
      return;
    }
    controller = new AbortController();
    estimateToken++;
    onStart?.();
    dom.run.disabled = true;
    dom.cancel.hidden = false;
    dom.progress.hidden = false;
    dom.progress.value = 0;
    dom.error.textContent = "";
    try {
      const result = await computeRainTotal({
        station: ctx().station,
        ...win,
        signal: controller.signal,
        onProgress: ({ phase, done, total }) => {
          dom.progress.max = total || 1;
          dom.progress.value = done;
          dom.estimate.textContent = phase === "frames" ? `Loading scans ${done}/${total}…`
            : phase === "flow" ? `Tracking storm motion ${done}/${total}…`
              : phase === "integrating" ? "Integrating…"
                : phase === "uncertainty" ? "Estimating uncertainty…" : "Finding scans…";
        },
      });
      onEnter?.();
      unpin(false);
      layer.setUnits(units);
      layer.show(result);
      gaugeStatus = { state: "idle" };
      renderSummary(result);
      dom.modal.hidden = true;
      onChange?.();
      loadGauges(result);
    } catch (err) {
      if (err?.name !== "AbortError") dom.error.textContent = err?.message || String(err);
    } finally {
      controller = null;
      dom.run.disabled = false;
      dom.cancel.hidden = true;
      dom.progress.hidden = true;
    }
  }

  dom.openBtn.addEventListener("click", open);
  dom.close.addEventListener("click", closeModal);
  dom.backdrop.addEventListener("click", closeModal);
  dom.cancel.addEventListener("click", () => controller?.abort());
  dom.run.addEventListener("click", () => run());
  dom.chartClose?.addEventListener("click", () => unpin());
  dom.start.addEventListener("change", refreshEstimate);
  dom.end.addEventListener("change", refreshEstimate);
  dom.summaryClose.addEventListener("click", exitRainMode);
  dom.units?.addEventListener("click", () => {
    units = units === "in" ? "mm" : "in";
    dom.units.textContent = units;
    layer.setUnits(units);
    gaugeLayer.setUnits(units);
    if (layer.result) renderSummary(layer.result);
    renderChart();
  });
  dom.gauges?.addEventListener("click", () => {
    gaugeLayer.setVisible(!gaugeLayer.visible);
    dom.gauges.classList.toggle("active", gaugeLayer.visible);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !dom.modal.hidden) closeModal();
  });

  // Shareable state for the URL, and a way to restore it.
  function shareState() {
    const result = layer.result;
    if (!result) return null;
    return { startMs: result.startMs, endMs: result.endMs, point: pinned ? { lat: pinned.latlng.lat, lon: pinned.latlng.lng } : null };
  }

  async function restore({ startMs, endMs, point }) {
    const station = ctx().station;
    if (!station) return;
    dom.station.textContent = station.label || station.id;
    dom.start.value = utcMsToZonedInput(startMs, station.timezone);
    dom.end.value = utcMsToZonedInput(endMs, station.timezone);
    dom.modal.hidden = false;
    await run({ startMs, endMs });
    if (point && layer.result) pinPoint(L.latLng(point.lat, point.lon));
  }

  return { open, exit: exitRainMode, restore, shareState, get active() { return Boolean(layer.result); } };
}
